// ── Google Tasks ⇄ Clarity Task sync engine ──────────────────────────────────
// Mirrors the shape of src/google/sync.js (Calendar events), but Google Tasks
// has no recurrence concept at all — one task = one title, one optional due
// DATE (no time-of-day), one completed/not-completed state. So only Clarity
// tasks with no recurrence (frequencyType "once" or unset) are synced;
// repeating tasks stay local-only, same philosophy as sync.js's "anything
// fancier than Daily/Weekly is left local-only" for events.
//
// Conflict model: Clarity tasks don't carry an `updatedAt` timestamp (unlike
// events), so instead of last-write-wins-by-time, we keep a snapshot of each
// task's synced field values. At sync time we diff the current local/Google
// values against that snapshot to see which side actually changed since the
// last sync. If both changed, Google's copy wins (it's the "external" system
// here — safer than silently overwriting an edit made in Google Tasks).

import { listTaskLists, listTasks, insertTask, patchTask, deleteTask } from './tasksApi';

const TRACK_KEY = 'clarity_gtaskSync';
const uid = () => 'gt' + Math.random().toString(36).slice(2, 10);

function isRecurring(t) {
  const ft = t.frequencyType;
  return Boolean(ft) && ft !== 'once';
}

// ── Clarity task → Google Tasks resource ─────────────────────────────────────

export function toGoogleTask(t) {
  const resource = {
    title: t.title || '(no title)',
    notes: t.notes || undefined,
    status: t.done ? 'completed' : 'needsAction',
  };
  const due = t.dueDate || t.startDate;
  // Google Tasks only respects the date part of `due` — it always resets the
  // time-of-day to midnight UTC, so there's no point sending anything else.
  if (due) resource.due = `${due}T00:00:00.000Z`;
  if (t.done) resource.completed = t.completedAt || new Date().toISOString();
  return resource;
}

// ── Google Tasks resource → Clarity task (partial; merged onto local) ────────

export function fromGoogleTask(g) {
  const due = g.due ? String(g.due).slice(0, 10) : '';
  return {
    title: g.title || '(no title)',
    notes: g.notes || '',
    dueDate: due,
    startDate: due,
    done: g.status === 'completed',
    completedAt: g.completed || null,
    // Sync bookkeeping (gtaskListId is set by the caller, which has it in scope)
    gtaskId: g.id,
  };
}

// Fields the snapshot/diff cares about — anything outside this set (subtasks,
// links, goalId, priority, color, reminders, ...) is Clarity-only and never
// round-trips through Google Tasks.
function fields(t) {
  return {
    title: t.title || '',
    notes: t.notes || '',
    dueDate: t.dueDate || t.startDate || '',
    done: Boolean(t.done),
  };
}
function fieldsFromGoogle(g) {
  return {
    title: g.title || '',
    notes: g.notes || '',
    dueDate: g.due ? String(g.due).slice(0, 10) : '',
    done: g.status === 'completed',
  };
}
function sameFields(a, b) {
  return a.title === b.title && a.notes === b.notes && a.dueDate === b.dueDate && a.done === b.done;
}

// ── Tracking store (per-task synced-field snapshot) ──────────────────────────

function loadTrack() {
  try { return JSON.parse(localStorage.getItem(TRACK_KEY) || 'null') || { taskListId: '', snapshots: {} }; }
  catch { return { taskListId: '', snapshots: {} }; }
}
function saveTrack(track) {
  try { localStorage.setItem(TRACK_KEY, JSON.stringify(track)); } catch {}
}
export function resetTaskTrack() { saveTrack({ taskListId: '', snapshots: {} }); }

// ── Public: list task lists (thin re-export for the hook) ────────────────────
export { listTaskLists };

// ── The orchestrator ─────────────────────────────────────────────────────────
//
// runTasksSync(tasks, taskListId) → { tasks, stats, changed }

export async function runTasksSync(tasks, taskListId) {
  const track = loadTrack();
  if (track.taskListId !== taskListId) { track.taskListId = taskListId; track.snapshots = {}; }

  const googleTasks = await listTasks(taskListId);

  const byGtask = new Map();
  for (const t of tasks) if (t.gtaskId) byGtask.set(t.gtaskId, t);

  let next = tasks.map((t) => ({ ...t }));
  const stats = { imported: 0, updated: 0, pushed: 0, deleted: 0 };
  const cancelledIds = new Set();

  // ── Google → local ──────────────────────────────────────────────────────────
  for (const g of googleTasks) {
    if (g.deleted) {
      cancelledIds.add(g.id);
      delete track.snapshots[g.id];
      const idx = next.findIndex((t) => t.gtaskId === g.id);
      if (idx !== -1) { next.splice(idx, 1); stats.deleted++; }
      continue;
    }
    const gFields = fieldsFromGoogle(g);
    const local = byGtask.get(g.id);
    const snap = track.snapshots[g.id];

    if (!local) {
      if (snap) {
        // We synced this task before, but it's no longer present locally —
        // the user deleted it in Clarity. Delete it on Google instead of
        // reimporting it as new (which would resurrect the deletion).
        try { await deleteTask(taskListId, g.id); stats.deleted++; } catch { /* leave for next sync */ }
        delete track.snapshots[g.id];
        continue;
      }
      // New task from Google — import it. Save its snapshot now (not just on
      // later syncs) — skipping this left the task with no recorded baseline,
      // so the very next sync saw both "sides" as changed relative to nothing
      // and had to guess, instead of cleanly detecting the real diff.
      next.push({
        id: uid(), frequencyType: 'once', frequencyDays: [], monthDates: [], yearDates: [],
        subtasks: [], links: [], createdAt: new Date().toISOString(),
        ...fromGoogleTask(g), gtaskListId: taskListId,
      });
      stats.imported++;
      track.snapshots[g.id] = gFields;
      continue;
    }

    if (isRecurring(local)) continue; // never touched by Tasks sync

    const localFields = fields(local);
    const googleChanged = !snap || !sameFields(gFields, snap);
    const localChanged = !snap || !sameFields(localFields, snap);

    if (googleChanged && !localChanged) {
      const idx = next.findIndex((t) => t.id === local.id);
      if (idx !== -1) { next[idx] = { ...next[idx], ...fromGoogleTask(g), gtaskListId: taskListId }; stats.updated++; }
    } else if (localChanged && !googleChanged) {
      try {
        const res = await patchTask(taskListId, g.id, toGoogleTask(local));
        stats.pushed++;
        if (res) track.snapshots[g.id] = fieldsFromGoogle(res);
      } catch { /* leave for next sync */ }
      continue; // snapshot already updated above
    } else if (localChanged && googleChanged) {
      // Both sides changed since last sync — Google wins.
      const idx = next.findIndex((t) => t.id === local.id);
      if (idx !== -1) { next[idx] = { ...next[idx], ...fromGoogleTask(g), gtaskListId: taskListId }; stats.updated++; }
    }
    track.snapshots[g.id] = gFields;
  }

  // ── Local → Google: brand-new local tasks (never synced, non-recurring) ─────
  for (const t of next) {
    if (t.gtaskId || isRecurring(t)) continue;
    if (!t.title) continue; // skip incomplete drafts
    try {
      const res = await insertTask(taskListId, toGoogleTask(t));
      if (res) {
        t.gtaskId = res.id;
        t.gtaskListId = taskListId;
        stats.pushed++;
        track.snapshots[res.id] = fieldsFromGoogle(res);
      }
    } catch { /* leave for next sync */ }
  }

  // ── Local deletions → Google ─────────────────────────────────────────────────
  // Any Google task id we had a snapshot for, that's no longer present locally
  // and wasn't already reported deleted by Google, was deleted here → delete
  // it on Google too.
  const presentGtask = new Set(next.filter((t) => t.gtaskId).map((t) => t.gtaskId));
  for (const gid of Object.keys(track.snapshots)) {
    if (!presentGtask.has(gid) && !cancelledIds.has(gid)) {
      try { await deleteTask(taskListId, gid); stats.deleted++; } catch { /* leave for next sync */ }
      delete track.snapshots[gid];
    }
  }

  track.taskListId = taskListId;
  saveTrack(track);

  const changed = stats.imported + stats.updated + stats.pushed + stats.deleted > 0;
  return { tasks: next, stats, changed };
}
